import { PickListFields } from "#src/routes/forms/categories/[categoryId]/create-form-template/steps/CreateFormTemplateSectionsStep/CreateFormTemplateAddQuestionDrawer/PickListFields";
import {
  Button,
  Dialog,
  DropdownInput,
  Form,
  RadioInput,
  TextAreaInput,
  TextInput,
  useForm,
  useHookWatch,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  PROPERTY_NAME_REGEX,
  ResourceLookup,
  type InputOutputSchemaPropertyConfigurationType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { snakeCase } from "es-toolkit/compat";
import React, { useEffect } from "react";
import styles from "./AddSchemaPropertyDialog.module.scss";
const cx = classNames.bind(styles);

const LOOKUP_ENTITY_TYPES = [
  ResourceLookup.ASSET_GROUP,
  ResourceLookup.DEVICE,
  ResourceLookup.EQUIPMENT,
  ResourceLookup.FACILITY,
  ResourceLookup.FLOW,
] as const;

const DATE_RESOLUTIONS = ["year", "year_month", "year_month_day"] as const;

const dataTypeOptions = Object.entries(AttributeDataType)
  .filter(([, value]) => value !== AttributeDataType.MULTI_FILE)
  .map(([key, value]) => ({
    label: toTitleCaseString(value, { intent: "ui" }),
    name: value,
    id: key,
  }));

const lookupEntityTypeOptions = LOOKUP_ENTITY_TYPES.map((value) => ({
  label: toTitleCaseString(value, { intent: "ui" }),
  value,
}));

const dateResolutionOptions = DATE_RESOLUTIONS.map((value) => ({
  label: toTitleCaseString(value, { intent: "ui" }),
  value,
}));

/**
 * Extra fields for pick list, lookup, and date data types. Used only by worker
 * version input/output schema (`AddSchemaPropertyDialog`).
 */
const ConditionalSchemaFields = ({
  lookupEntityTypeOptions,
  dateResolutionOptions,
}: {
  lookupEntityTypeOptions: Array<{ label: string; value: string }>;
  dateResolutionOptions: Array<{ label: string; value: string }>;
}) => {
  const watchedDataType = useHookWatch({ name: "data_type" });
  const isPicklist =
    watchedDataType === AttributeDataType.PICK_LIST ||
    watchedDataType === AttributeDataType.MULTI_PICK_LIST;
  const isLookup =
    watchedDataType === AttributeDataType.LOOKUP ||
    watchedDataType === AttributeDataType.MULTI_LOOKUP;
  const isDate = watchedDataType === AttributeDataType.DATE;

  return (
    <>
      {isPicklist ? <PickListFields /> : null}
      {isLookup ? (
        <DropdownInput
          name="lookup_entity_type"
          label="Lookup Entity Type"
          options={lookupEntityTypeOptions}
          valueKey="value"
          labelKey="label"
          isRequired
        />
      ) : null}
      {isDate ? (
        <DropdownInput
          name="date_resolution"
          label="Date Resolution"
          options={dateResolutionOptions}
          valueKey="value"
          labelKey="label"
          isOptionalTextShown
        />
      ) : null}
    </>
  );
};

type AddSchemaPropertyFormValuesType =
  InputOutputSchemaPropertyConfigurationType & {
    property_key: string;
  };

/**
 * Dialog to add or edit a single worker version input/output schema property.
 * For lookup types, only `lookup_entity_type` is collected (required); attribute
 * fields are not used for workers. Output schema uses the label “value must be present”
 * for the `is_required` control when `isOutputSchema` is true.
 */
export const AddSchemaPropertyDialog = ({
  isOpen,
  onClose,
  onSubmit,
  existingPropertyKeys = [],
  editingProperty = null,
  isOutputSchema = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    propertyKey: string,
    config: InputOutputSchemaPropertyConfigurationType
  ) => void;
  existingPropertyKeys?: string[];
  editingProperty?: {
    propertyKey: string;
    config: InputOutputSchemaPropertyConfigurationType;
  } | null;
  /** When true (output schema step), the requiredness control uses output-oriented copy. */
  isOutputSchema?: boolean;
}) => {
  const isEditMode = Boolean(editingProperty);

  const form = useForm<AddSchemaPropertyFormValuesType>({
    mode: "onChange",
    defaultValues: {
      property_key: "",
      display_name: "",
      order: 0,
      description: "",
      data_type: AttributeDataType.STRING,
      is_required: false,
    },
  });

  const watchedDisplayName = form.watch("display_name");
  const watchedDataType = form.watch("data_type");

  /**
   * Worker schema only persists `lookup_entity_type` for lookup fields; clear
   * stale values when the data type changes and avoid sending legacy attribute
   * fields from the payload.
   */
  useEffect(() => {
    const isLookup =
      watchedDataType === AttributeDataType.LOOKUP ||
      watchedDataType === AttributeDataType.MULTI_LOOKUP;
    if (isLookup) return;
    form.setValue("lookup_entity_type", undefined, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [watchedDataType, form]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.handleSubmit((values) => {
    const watchedDataType = values.data_type;
    const isPicklist =
      watchedDataType === AttributeDataType.PICK_LIST ||
      watchedDataType === AttributeDataType.MULTI_PICK_LIST;
    const isLookup =
      watchedDataType === AttributeDataType.LOOKUP ||
      watchedDataType === AttributeDataType.MULTI_LOOKUP;
    const isDate = watchedDataType === AttributeDataType.DATE;

    const { property_key, ...configValues } = values;

    const keysToCheck = isEditMode
      ? existingPropertyKeys.filter((k) => k !== editingProperty?.propertyKey)
      : existingPropertyKeys;
    const baseKey = property_key || snakeCase(configValues.display_name);
    let uniqueKey = baseKey;
    let suffix = 0;
    while (keysToCheck.includes(uniqueKey)) {
      suffix += 1;
      uniqueKey = `${baseKey}_${suffix}`;
    }

    const config: InputOutputSchemaPropertyConfigurationType = {
      display_name: configValues.display_name,
      order: 0,
      data_type: configValues.data_type,
      description: configValues.description ?? "",
      is_required: configValues.is_required ?? false,
    };

    if (isPicklist && configValues.pick_list_values) {
      config.pick_list_values = configValues.pick_list_values;
    }
    if (isLookup) {
      config.lookup_entity_type = configValues.lookup_entity_type;
    }
    if (isDate && configValues.date_resolution) {
      config.date_resolution = configValues.date_resolution;
    }

    onSubmit(uniqueKey, config);

    handleClose();
  });

  useEffect(() => {
    if (isEditMode) return;
    if (form.formState.dirtyFields.property_key) return;

    form.setValue("property_key", snakeCase(watchedDisplayName || ""), {
      shouldDirty: false,
    });
  }, [watchedDisplayName, isEditMode]);

  useEffect(() => {
    if (!isOpen) return;

    if (editingProperty) {
      form.reset({
        property_key: editingProperty.propertyKey,
        display_name: editingProperty.config.display_name ?? "",
        order: 0,
        description: editingProperty.config.description ?? "",
        data_type: editingProperty.config.data_type ?? AttributeDataType.STRING,
        is_required: editingProperty.config.is_required ?? false,
        lookup_entity_type:
          editingProperty.config.lookup_entity_type ?? undefined,
        date_resolution: editingProperty.config.date_resolution ?? undefined,
        pick_list_values: editingProperty.config.pick_list_values ?? [],
      });
    } else {
      form.reset();
    }
  }, [isOpen, editingProperty]);

  return (
    <Dialog
      title={isEditMode ? "Edit Schema Property" : "Add Schema Property"}
      isOpen={isOpen}
      onClose={handleClose}
      actionRow={[
        <Button
          key="add-schema-property"
          variant="primary"
          onClick={() => void handleSubmit()}
          disabled={!form.formState.isValid}
        >
          {isEditMode ? "Save" : "Add"}
        </Button>,
      ]}
      dialogClassName={cx("add-schema-property-dialog")}
    >
      <Form {...form}>
        <TextInput
          name="display_name"
          label="Display Name"
          isRequired
          type="text"
        />
        <TextInput
          name="property_key"
          label="Property Name"
          isRequired
          type="text"
          rules={{
            validate: {
              regex: (value) => {
                return (
                  PROPERTY_NAME_REGEX.test(value) ||
                  "Property name can only contain  alphanumeric characters, hyphens, or underscores"
                );
              },
            },
          }}
        />
        <TextAreaInput
          name="description"
          label="Description"
        />
        <RadioInput
          name="is_required"
          label={
            isOutputSchema ? "Output value must be present" : "Is Required"
          }
          isRequired
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
          valueKey="value"
          labelKey="label"
        />
        <DropdownInput
          name="data_type"
          label="Data Type"
          options={dataTypeOptions}
          valueKey="name"
          labelKey="label"
          isRequired
        />
        <ConditionalSchemaFields
          lookupEntityTypeOptions={lookupEntityTypeOptions}
          dateResolutionOptions={dateResolutionOptions}
        />
      </Form>
    </Dialog>
  );
};
